import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Building2, Users, DollarSign, Calendar, TrendingUp, Tag } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export default function DashboardView({ stats, setActiveSection }) {
  return (
    <>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Platform Overview</h1>
        <p className="text-gray-600">Manage clinics and monitor platform-wide analytics</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Clinics</CardDescription>
            <CardTitle className="text-3xl">{stats?.total_clinics || 0}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center text-sm text-green-600">
              <Building2 className="h-4 w-4 mr-1" />
              <span>{stats?.active_clinics || 0} active</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Members</CardDescription>
            <CardTitle className="text-3xl">{stats?.total_members || 0}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center text-sm text-gray-600">
              <Users className="h-4 w-4 mr-1" />
              <span>{stats?.active_members || 0} active</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Revenue</CardDescription>
            <CardTitle className="text-3xl">${stats?.total_revenue?.toFixed(2) || 0}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center text-sm text-gray-600">
              <DollarSign className="h-4 w-4 mr-1" />
              <span>Platform-wide</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Appointments</CardDescription>
            <CardTitle className="text-3xl">{stats?.total_appointments || 0}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center text-sm text-gray-600">
              <Calendar className="h-4 w-4 mr-1" />
              <span>All clinics</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Growth Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Card>
          <CardHeader>
            <CardTitle>Clinic Growth</CardTitle>
            <CardDescription>New clinics over time</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={stats?.clinic_growth || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#14b8a6" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Member Growth</CardTitle>
            <CardDescription>New members across platform</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={stats?.member_growth || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#06b6d4" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Common administrative tasks</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button 
              onClick={() => setActiveSection('clinics')}
              className="h-24 flex flex-col items-center justify-center gap-2"
              variant="outline"
            >
              <Building2 className="h-8 w-8 text-teal-600" />
              <span className="font-semibold">Manage Clinics</span>
              <span className="text-xs text-gray-500">{stats?.total_clinics || 0} clinics</span>
            </Button>
            
            <Button 
              onClick={() => setActiveSection('analytics')}
              className="h-24 flex flex-col items-center justify-center gap-2"
              variant="outline"
            >
              <TrendingUp className="h-8 w-8 text-blue-600" />
              <span className="font-semibold">View Analytics</span>
              <span className="text-xs text-gray-500">Platform insights</span>
            </Button>
            
            <Button 
              onClick={() => setActiveSection('pricing')}
              className="h-24 flex flex-col items-center justify-center gap-2"
              variant="outline"
            >
              <Tag className="h-8 w-8 text-purple-600" />
              <span className="font-semibold">Pricing Tiers</span>
              <span className="text-xs text-gray-500">Manage plans</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
